import {
  useState,
  useEffect,
  useRef,
  useContext,
  createContext,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import toast from "react-hot-toast";
import { teamService } from "../services/partnerService";
import { authService } from "../services/authService";
import { isPlatformAdmin } from "../utils/roles";
import { posthog } from "../instrument";
import { getLocalTimezone } from "../utils/timezone";

const AuthContext = createContext({});

const VIEW_AS_KEY = "viewAsRole";
const VIEW_AS_ROLES = {
  admin: { label: "Admin", icon: "Shield", color: "indigo" },
  student: { label: "Student", icon: "GraduationCap", color: "blue" },
  instructor: { label: "Instructor", icon: "BookOpen", color: "emerald" },
  partner: { label: "Partner", icon: "Users", color: "purple" },
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const userProfileRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);
  const [session, setSession] = useState(null);
  const navigate = useNavigate();

  // ── View As role state ──
  const [viewAsRole, setViewAsRoleRaw] = useState(() => {
    try {
      return sessionStorage.getItem(VIEW_AS_KEY) || null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    // Handle cross-subdomain auth tokens passed via URL hash
    const handleCrossSubdomainAuth = async () => {
      const hash = window.location.hash.substring(1);
      if (!hash || !hash.includes("access_token")) return null;

      const params = new URLSearchParams(hash);
      const accessToken = params.get("access_token");
      const refreshToken = params.get("refresh_token");

      if (!accessToken || !refreshToken) {
        window.history.replaceState(
          null,
          "",
          window.location.pathname + window.location.search,
        );
        return null;
      }

      try {
        const { data, error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (!error && data.session) return data.session;
      } catch (err) {
        console.error("Cross-subdomain auth failed:", err);
      } finally {
        // Clean token-bearing hash even if session hydration fails.
        window.history.replaceState(
          null,
          "",
          window.location.pathname + window.location.search,
        );
      }
      return null;
    };

    // Get initial session
    const getSession = async () => {
      try {
        // One-time migration: clear stale sessions from old Supabase project (awkmzskpurhgfanqhcge)
        // After migrating to new project (gworqjlrbwdihgiwmvnu), old JWTs are invalid.
        // This silently clears them so users get a clean login prompt instead of cryptic errors.
        const MIGRATION_KEY = "supabase_project_migrated_v1";
        if (!localStorage.getItem(MIGRATION_KEY)) {
          localStorage.setItem(MIGRATION_KEY, "true");
          // Check if there's an existing session with the old project's URL
          const storedSession = localStorage.getItem(
            "sb-awkmzskpurhgfanqhcge-auth-token",
          );
          if (storedSession) {
            // Old project session found — clear all auth state
            localStorage.removeItem("sb-awkmzskpurhgfanqhcge-auth-token");
            await supabase.auth.signOut({ scope: "local" });
            setUser(null);
            setUserProfile(null);
            setLoading(false);
            setProfileLoading(false);
            return;
          }
        }

        // First check for cross-subdomain tokens in URL hash
        const crossSubSession = await handleCrossSubdomainAuth();
        if (crossSubSession) {
          setSession(crossSubSession);
          setUser(crossSubSession.user);
          await fetchUserProfile(crossSubSession.user.id);
          setLoading(false);
          return;
        }

        const session = await authService.getSession();

        setSession(session);
        setUser(session?.user || null);
        if (session?.user) {
          await fetchUserProfile(session.user.id);
        } else {
          setProfileLoading(false);
        }
      } catch (err) {
        console.error("Session check failed", err);
        setProfileLoading(false);
      } finally {
        setLoading(false);
      }
    };

    getSession();

    // Helper function to check partner status before redirecting
    const checkPartnerAndRedirect = async (userId, redirectTo) => {
      try {
        // We use a toast to inform the user why they were redirected elsewhere
        const { team } = await teamService.getTeamForUser(userId);

        if (team && team.status === "active") {
          navigate(redirectTo);
        } else if (team && team.status === "pending") {
          toast.error("Your partner account is pending approval.");
          navigate("/user/profile");
        } else {
          toast.error(
            "You need an active partner account to login as a partner.",
          );
          navigate("/user/profile");
        }
      } catch (error) {
        console.error("Error checking partner status:", error);
        navigate("/user/profile");
      }
    };

    // Listen for auth changes
    const {
      data: { subscription },
    } = authService.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user || null);
      setLoading(false);

      // Handle sign out / token expiry
      if (
        event === "SIGNED_OUT" ||
        (!session?.user && event === "TOKEN_REFRESHED")
      ) {
        setUserProfile(null);
        userProfileRef.current = null;
        setProfileLoading(false);
        // Only show expiry toast on token refresh failure, not on manual/normal sign-out
        if (event === "TOKEN_REFRESHED") {
          toast?.error("Your session has expired. Please sign in again.");
        }
        navigate("/login");
        return;
      }

      // Fetch user profile when signed in
      if (session?.user) {
        // Only set loading if we don't have a profile or it's a different user
        if (
          !userProfileRef.current ||
          userProfileRef.current.id !== session.user.id
        ) {
          setProfileLoading(true);
          fetchUserProfile(session.user.id);
        }
      } else {
        setUserProfile(null);
        userProfileRef.current = null;
        setProfileLoading(false);
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  const fetchUserProfile = useCallback(async (userId, retryCount = 0) => {
    try {
      const { data, error } = await supabase
        .from("user_profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (error && error.code !== "PGRST116") {
        if (retryCount < 1) {
          console.warn("Profile fetch failed, retrying...", error);
          setTimeout(() => fetchUserProfile(userId, 1), 1500);
          return;
        }
        console.error("Error fetching user profile after retry:", error);
        toast?.error("Failed to load your profile. Please refresh the page.");
        setProfileLoading(false);
        return;
      }

      const localTimezone = getLocalTimezone();
      const profile = data && localTimezone
        ? { ...data, timezone: localTimezone }
        : data;
      setUserProfile(profile);
      userProfileRef.current = profile;
      setProfileLoading(false);

      const timezoneStorageKey = `browser_timezone:${userId}`;
      let savedBrowserTimezone = null;
      try {
        savedBrowserTimezone = localStorage.getItem(timezoneStorageKey);
      } catch {}

      if (data && localTimezone && savedBrowserTimezone !== localTimezone) {
        void supabase
          .from("user_timezone_preferences")
          .upsert(
            {
              user_id: userId,
              timezone: localTimezone,
              updated_at: new Date().toISOString(),
            },
            { onConflict: "user_id" },
          )
          .then(({ error: timezoneError }) => {
            if (timezoneError) {
              console.warn("Unable to save browser timezone:", timezoneError);
              return;
            }
            try {
              localStorage.setItem(timezoneStorageKey, localTimezone);
            } catch {}
          });
      }

      posthog.identify(userId, {
        email: data?.email,
        name: data?.full_name,
        role: data?.role,
      });
    } catch (error) {
      if (retryCount < 1) {
        console.warn("Profile fetch exception, retrying...", error);
        setTimeout(() => fetchUserProfile(userId, 1), 1500);
        return;
      }
      console.error("Error fetching user profile after retry:", error);
      toast?.error("Failed to load your profile. Please refresh the page.");
      setProfileLoading(false);
    }
  }, []);

  const checkAdminAccess = useCallback(async () => {
    try {
      if (!user) {
        return {
          hasAccess: false,
          loading: false,
          error: "Authentication required",
        };
      }

      // userProfile is already loaded by AuthProvider — no need to re-query
      const isAdmin = isPlatformAdmin(user, userProfile);
      return {
        hasAccess: isAdmin,
        loading: false,
        error: isAdmin ? null : "Admin privileges required",
      };
    } catch (error) {
      return {
        hasAccess: false,
        loading: false,
        error: "Access verification failed",
      };
    }
  }, [user, userProfile]);

  const signInWithMagicLink = useCallback(async (email) => {
    try {
      setLoading(true);
      const data = await authService.signInWithOtp(email);
      return { data, error: null };
    } catch (error) {
      console.error("Error sending authentication code:", error);
      toast?.error(error?.message || "Failed to send authentication code");
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyOtp = useCallback(async (email, token, type = "email") => {
    try {
      setLoading(true);
      const data = await authService.verifyOtp(email, token, type);
      return { data, error: null };
    } catch (error) {
      console.error("Error verifying token:", error);
      toast?.error(error?.message || "Invalid authentication code");
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      posthog.capture("user_logged_out");
      posthog.reset();
      await authService.signOut();
    } catch (error) {
      console.error("Error signing out:", error);
      toast?.error("Error signing out");
    }
  }, []);

  const signUpWithPassword = useCallback(async (email, password, userData = {}) => {
    try {
      setLoading(true);
      const { data, error } = await authService.signUpWithPassword(
        email,
        password,
        userData,
      );
      if (error) {
        toast?.error(error?.message || "Failed to create account");
        return { data: null, error };
      }
      return { data, error: null };
    } catch (error) {
      console.error("Error signing up:", error);
      toast?.error(error?.message || "Failed to create account");
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  }, []);

  const signInWithGoogle = useCallback(async (redirectTo) => {
    try {
      const data = await authService.signInWithGoogle(redirectTo);
      return { data, error: null };
    } catch (error) {
      console.error("Error signing in with Google:", error);
      toast?.error(error?.message || "Failed to sign in with Google");
      return { data: null, error };
    }
  }, []);

  const signInWithPassword = useCallback(async (email, password) => {
    try {
      setLoading(true);
      const { data, error } = await authService.signInWithPassword(
        email,
        password,
      );
      if (error) {
        toast?.error(error?.message || "Invalid email or password");
        return { data: null, error };
      }
      posthog.capture("user_logged_in", { method: "password" });
      return { data, error: null };
    } catch (error) {
      console.error("Error signing in:", error);
      toast?.error(error?.message || "Failed to sign in");
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  }, []);

  const resendVerificationEmail = useCallback(async (email) => {
    try {
      const { error } = await authService.resendVerificationEmail(email);
      if (error) {
        toast?.error(error?.message || "Failed to resend email");
        return { error };
      }
      toast?.success("Verification email sent!");
      return { error: null };
    } catch (error) {
      console.error("Error resending verification:", error);
      toast?.error(error?.message || "Failed to resend email");
      return { error };
    }
  }, []);

  // ── View As: computed values ──
  const isRealAdmin = isPlatformAdmin(user, userProfile);

  // Clear viewAs if user signs out or isn't actually an admin
  useEffect(() => {
    if (!user || !isRealAdmin) {
      setViewAsRoleRaw(null);
      try {
        sessionStorage.removeItem(VIEW_AS_KEY);
      } catch {}
    }
  }, [user, isRealAdmin]);

  const setViewAsRole = useCallback(
    (role) => {
      if (!isRealAdmin) return;
      const newRole = role === "admin" ? null : role;
      setViewAsRoleRaw(newRole);
      try {
        if (newRole) sessionStorage.setItem(VIEW_AS_KEY, newRole);
        else sessionStorage.removeItem(VIEW_AS_KEY);
      } catch {}
    },
    [isRealAdmin],
  );

  const exitViewAs = useCallback(() => {
    setViewAsRoleRaw(null);
    try {
      sessionStorage.removeItem(VIEW_AS_KEY);
    } catch {}
  }, []);

  const isViewingAs = isRealAdmin && viewAsRole != null;
  const effectiveRole = isViewingAs
    ? viewAsRole
    : userProfile?.role || "student";

  // When ViewAs is active, override the profile role. Auth metadata is not an
  // authorization source because user_metadata can be client-controlled.
  const effectiveUserProfile = useMemo(() => {
    if (!isViewingAs || !userProfile) return userProfile;
    return { ...userProfile, role: viewAsRole };
  }, [userProfile, isViewingAs, viewAsRole]);

  const effectiveUser = user;

  const value = useMemo(() => ({
    // These are overridden when ViewAs is active — all pages automatically respect it
    user: effectiveUser,
    userProfile: effectiveUserProfile,
    session,
    loading,
    profileLoading,
    signInWithMagicLink,
    signInWithGoogle,
    verifyOtp,
    signUpWithPassword,
    signInWithPassword,
    resendVerificationEmail,
    checkAdminAccess,
    fetchUserProfile,
    signOut,
    // ViewAs API
    realUser: user,
    realUserProfile: userProfile,
    isRealAdmin,
    viewAsRole,
    setViewAsRole,
    exitViewAs,
    isViewingAs,
    effectiveRole,
    VIEW_AS_ROLES,
  }), [
    effectiveUser,
    effectiveUserProfile,
    session,
    loading,
    profileLoading,
    signInWithMagicLink,
    signInWithGoogle,
    verifyOtp,
    signUpWithPassword,
    signInWithPassword,
    resendVerificationEmail,
    checkAdminAccess,
    fetchUserProfile,
    signOut,
    user,
    userProfile,
    isRealAdmin,
    viewAsRole,
    setViewAsRole,
    exitViewAs,
    isViewingAs,
    effectiveRole,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
